import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { isBrandAC } from 'src/constants/main';
import { IMyOrder } from '../../../redux/user/user.duck.interface';
import DealFinancePrice from './components/DealFinancePrice';
import { getImageUrl, renderImage } from './components/Image';
import { renderDealStepsFlow } from './components/renderDealStapsFlow';
import { createMyOrdersDealSteps } from './components/dealSteps.utils';
import { OrderNumberComponent } from './components/OrderNumberComponent';
import { useTranslations } from '@hooks/useTranslations';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { OrderCompletedMopLastStepCode } from '@hooks/usePxPendingUploads';
import { MySummary } from '../../MySummary/MySummary';
import { renderCarName } from './components/renderCarName';
import Icon, { Icons } from '../../Icon/index';
import { useDispatch } from 'react-redux';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { PxStatus } from '../../../partExchange/constants/config';
import PartExchangeWidget from '@components/PartExchange/PartExchangeWidget';
import DealCashPrice from './components/DealCashPrice';
import { formatLeadTime } from '@utils/Date.utils';
import { Pages } from '../../../types/pages';

interface IMyOrderProps {
    myOrder: IMyOrder;
}

const MyOrder: FC<IMyOrderProps> = memo(({ myOrder }: IMyOrderProps) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const [isExtendedViewOpen, setIsExtendedViewOpen] = useState(false);

    const { sibling } = myOrder;

    const toggleExtendedView = useCallback(() => {
        setIsExtendedViewOpen(!isExtendedViewOpen);
        isExtendedViewOpen
            ? dispatch(UIDuck.closeCharacteristics())
            : dispatch(UIDuck.openCharacteristics({ data: {}, modalType: '' }));
    }, [dispatch, isExtendedViewOpen]);

    const pendingPxUpload = useMemo(() => {
        if (
            myOrder.sibling.partExchange !== null &&
            myOrder.sibling.extraFields.mopLastStepCode === OrderCompletedMopLastStepCode
        ) {
            if (myOrder.sibling.extraFields.partExchangeStatusId >= PxStatus.COMPLETED) {
                return true;
            } else {
                if (myOrder.sibling.extraFields.partExchangeStatusId === PxStatus.PICTURE) {
                    return false;
                } else {
                    return undefined;
                }
            }
        } else {
            return undefined;
        }
    }, [myOrder]);

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);

    return (
        <div className="deal">
            <div className="deal-content">
                <OrderNumberComponent orderNumber={myOrder.orderNumber} orderDate={myOrder.dateAdded} />

                <div className="car-header">
                    <div className="title">{renderCarName(sibling.fullProductConfiguration)}</div>
                    <div className="price-and-date">
                        <div className="price-container">
                            {sibling.financeQuote ? (
                                <DealFinancePrice
                                    id={sibling.financeQuote}
                                    fullProductConfiguration={sibling.fullProductConfiguration}
                                    dealerId={sibling.dealer}
                                />
                            ) : (
                                <DealCashPrice
                                    prices={sibling.fullProductConfiguration.pricesV2}
                                    fullProductConfiguration={sibling.fullProductConfiguration}
                                />
                            )}
                        </div>
                        <div className="date">
                            <span className="date-clock-icon">
                                <Icon name={isBrandAC ? Icons.ClockSolid : Icons.Clock} height={16} width={16} />
                            </span>
                            <span>{formatLeadTime(sibling.fullProductConfiguration.leadTimeDate)}</span>
                        </div>
                    </div>
                </div>
                <div className="content">
                    {renderImage(getImageUrl(sibling.fullProductConfiguration))}
                    {renderDealStepsFlow(createMyOrdersDealSteps(t, pendingPxUpload))}
                </div>
                {isSOLEnabled && (
                    <div className="view-more">
                        <button className="details-link" onClick={toggleExtendedView}>
                            {t('checkout.myAccount.viewAllDetails')}
                        </button>
                    </div>
                )}

                {pendingPxUpload !== undefined &&
                    !pendingPxUpload &&
                    myOrder.sibling.extraFields.partExchangeStatusId === PxStatus.PICTURE && (
                        <div className="pxWrapper">
                            <PartExchangeWidget
                                car={myOrder.sibling.fullProductConfiguration}
                                mopId={myOrder.sibling.mopId}
                                dealId={myOrder.sibling?.extraFields?.dealID}
                                partExchangeId={myOrder.sibling.partExchange}
                                allowPxFinalization
                                hideAdditionalInfo
                                asUploadButton
                            />
                        </div>
                    )}
            </div>

            {!isSOLEnabled && (
                <MySummary
                    carDetails={sibling.fullProductConfiguration}
                    dealerId={sibling.dealer}
                    showFinanceWidget={false}
                    displayPx={false}
                    order={myOrder}
                    isMyOrderPage
                />
            )}

            {isExtendedViewOpen && (
                <MySummary
                    carDetails={sibling.fullProductConfiguration}
                    dealerId={sibling.dealer}
                    showFinanceWidget={false}
                    displayPx={false}
                    order={myOrder}
                    isMyOrderPage
                />
            )}
        </div>
    );
});
MyOrder.displayName = 'MyOrder';
export default MyOrder;
